import { Role } from "types/Role";
import { isMaster } from "utils/onboardingPermissions";

/**
 * «Vadovas priima darbuotojus ir mato komandos duomenis» — the department
 * form's rules, kept pure for the tests. Owner decision (2026-10-12): the flag
 * lets the head lead hiring and see the team in Personalas, so only a Master
 * may turn it on; anyone who edits the department may turn it off.
 */

export function canGrantHeadDataAccess(role?: Role | null): boolean {
  return isMaster(role);
}

export interface HeadDataAccessState {
  canGrant: boolean;
  /** Selected head (CompaniesUsers id), "" for none. */
  head: string;
  checked: boolean;
  /** Head and flag as loaded from the saved department. */
  savedHead: string;
  savedHeadHadAccess: boolean;
}

/** Checkbox value after the head select changes: a new head starts without access, the saved head gets its saved value back. */
export function accessAfterHeadChange(nextHead: string, savedHead: string, savedHeadHadAccess: boolean): boolean {
  return !!nextHead && nextHead === savedHead ? savedHeadHadAccess : false;
}

/** The saved head keeps the access it already had (ticking back an own untick is not a grant). */
const keepsSavedAccess = (s: HeadDataAccessState) => s.savedHeadHadAccess && s.head === s.savedHead;

export type HeadAccessBlock = "no_head" | "master_only" | null;

/** Why the checkbox cannot be ticked now (null = it can be toggled). Unticking is always allowed. */
export function headAccessBlock(s: HeadDataAccessState): HeadAccessBlock {
  if (!s.head) return "no_head";
  if (s.checked || keepsSavedAccess(s)) return null;
  if (!s.canGrant) return "master_only";
  return null;
}

/**
 * The `headHasDataAccess` to send. A Master sends it explicitly (the backend
 * honours an explicit value, also on a head change). Anyone else only sends a
 * revoke of the saved head's access; otherwise the field is left out and the
 * backend keeps the flag, or resets it on a head change.
 */
export function headDataAccessPayload(s: HeadDataAccessState): boolean | undefined {
  if (s.canGrant) return !!s.head && s.checked;
  if (keepsSavedAccess(s) && !s.checked) return false;
  return undefined;
}

/** The checkbox is being turned on now (not just kept for the saved head). */
export function isGrantingAccess(s: HeadDataAccessState): boolean {
  return !!s.head && s.checked && !keepsSavedAccess(s);
}

/**
 * How many people the head would see: members of this department (as edited)
 * plus members of its sub-departments. null when the sub-departments are not
 * known — the form then shows a generic warning instead of a number.
 */
export function countHeadVisiblePeople(
  memberIds: string[],
  subgroups: { members?: { companyUserId: string }[] }[] | null | undefined
): number | null {
  if (!subgroups) return null;
  const ids = new Set(memberIds);
  for (const subgroup of subgroups) {
    for (const member of subgroup.members || []) ids.add(member.companyUserId);
  }
  return ids.size;
}
